import React, { useCallback, useMemo, useRef, useState } from "react"
import { Button, Input, Modal, Segmented, Table } from "antd"
import type { CheckboxProps } from "antd"
import type { ColumnsType } from "antd/es/table"
import { dataVariableParts, describeDataVariable } from "../../model/dataVariables"
import { dataTablesFor } from "../../model/defaults"
import { shownNames } from "../../model/shownLabels"
import { useTabSpecStore } from "../../runtime/tabSpecStore"
import type { Catalogue, CatalogueQuestion } from "../../types/catalogue"
import type { ShownLabel, TableSpec, TabSpecDefaults } from "../../types/tabSpecDocument"
import { SPEC_DIALOG_Z_INDEX } from "../Header/SpecHeader.component"
import { dataNotReady } from "./dataState"

/** Which variables the list shows: every one, or those the spec has no table of. */
type Listing = "all" | "untabled"
/** What the tables of sets of yes or no fields are made: multiple or single choice. */
type ShowAs = "multi" | "categorical"

const LISTINGS: { value: Listing; label: string }[] = [
  { value: "all", label: "All variables" },
  { value: "untabled", label: "Without a table" },
]
const SHOW_AS: { value: ShowAs; label: string }[] = [
  { value: "multi", label: "Multiple choice" },
  { value: "categorical", label: "Single choice" },
]

const HINT_DATA_ONLY =
  "One table per variable, or one per row of a grid, with the spec's defaults. The tables are added at the end, in the order of the data."
const HINT_QUESTIONNAIRE =
  "Variables of the prepared data that are not in the questionnaire. One table per variable, or one per row of a grid, with the spec's defaults."
const NONE_DATA_ONLY = "The prepared data has no variable that can be made into a table."
const NONE_QUESTIONNAIRE = "The prepared data has no variable outside the questionnaire."
const SINGLE_HINT =
  "Each table checks, when it runs, that every respondent has exactly one category marked, and says so when not."
/** `dataTablesFor` makes a variable the codebook calls "single" single choice whatever is chosen. */
const SINGLE_IN_DATA =
  "A variable the data marks as single choice is added as single choice whatever is chosen here."

/** The list's fixed row height: every row is one line (tabSpec.css). */
const ROW_HEIGHT = 40
const ROW_STYLE: React.CSSProperties = { height: ROW_HEIGHT }
/** As the grid of tables passes it: antd hands it on to rc-table's virtual table. */
const VIRTUAL_ROWS = { listItemHeight: ROW_HEIGHT }
const SCROLL = { y: 360 }
const TICK_WIDTH = 32

/** One variable of the list; the object is kept while nothing it shows changes. */
interface VariableRow {
  /** The raw label: the variable's identity. */
  key: string
  question: CatalogueQuestion
  /** The main text: the shown label, or the last part of the variable's path. */
  name: string
  /** The containers above it in words, muted; null at the top level or under a shown label. */
  within: string | null
  /** The whole raw label, as the row's tooltip. */
  title: string
  phrase: string
  /** Tables the spec has of the variable. */
  tables: number
  /** Tables Add makes of it. */
  adds: number
  /** The raw and the shown label, lower case, for the search. */
  search: string
}

const plural = (n: number, one: string, many: string): string =>
  n === 1 ? `1 ${one}` : `${n} ${many}`

/** "No variable chosen", or "12 variables chosen, 15 tables". */
export const chosenText = (variables: number, tables: number): string =>
  variables === 0
    ? "No variable chosen"
    : `${plural(variables, "variable", "variables")} chosen, ${plural(tables, "table", "tables")}`

/** The variables listed that "Select all listed" leaves out because they have tables. */
export const tabledText = (n: number): string =>
  n === 1 ? "1 already has a table." : `${n} already have a table.`

/** The grids across containers listed, which "Select all listed" leaves out. */
export const acrossText = (n: number): string =>
  n === 1
    ? "1 is a grid across containers: tick it on its own."
    : `${n} are grids across containers: tick them on their own.`

/** Whether "Select all listed" ticks the row: no table yet, and not a grid across containers. */
const inBulk = (r: VariableRow): boolean => r.tables === 0 && !r.question.siblings

const sameRow = (a: VariableRow, b: VariableRow): boolean =>
  a.question === b.question &&
  a.name === b.name &&
  a.within === b.within &&
  a.phrase === b.phrase &&
  a.tables === b.tables &&
  a.adds === b.adds &&
  a.search === b.search

const baseColumns: ColumnsType<VariableRow> = [
  {
    title: "Variable",
    key: "variable",
    render: (_, row) => (
      <div className="ts-build__variable" title={row.title}>
        <span className="ts-build__name">{row.name}</span>
        {row.within && <span className="ts-build__within">{row.within}</span>}
        {row.tables > 0 && (
          <span className="ts-chip ts-build__chip">{plural(row.tables, "table", "tables")}</span>
        )}
      </div>
    ),
  },
  {
    title: "Kind",
    key: "kind",
    width: 140,
    render: (_, row) => (
      <span className="ts-build__phrase" title={row.phrase}>
        {row.phrase}
      </span>
    ),
  },
]
// A cell renders again only for a new row object (see `rows` below).
const columns: ColumnsType<VariableRow> = baseColumns.map((column) => ({
  ...column,
  shouldCellUpdate: (a: VariableRow, b: VariableRow) => a !== b,
}))

const onRow = (): React.HTMLAttributes<HTMLElement> => ({ style: ROW_STYLE })

/** The lists read while the dialog is closed: constants, so a closed dialog never renders again. */
const NO_TABLES: TableSpec[] = []

export interface BuildFromDataDialogProps {
  open: boolean
  onClose: () => void
  /** Variables to list (the finding's); null = every data variable. */
  only?: string[] | null
  /** Where the dialog renders; the tab spec's root, so it gets the --qc-* tokens. */
  getContainer?: () => HTMLElement
}

/**
 * "Build tables from the data": every variable of the prepared data (the
 * weight left out) in the order of the data, each with what it is and how
 * many tables the spec already has of it. The user ticks some (a search, the
 * variables without a table, "Select all listed") and Add makes their
 * tables with the spec's defaults, at the end and in the order of the data,
 * in one write; nothing is selected, so the inspector stays closed. "Select
 * all listed" takes the variables listed that have no table yet and never a
 * grid across containers (whose rows are variables listed on their own), and
 * the dialog says how many it leaves out. A set of
 * yes or no fields can be made single choice ("Show categories as"). The
 * list is virtual with rows of one height, and a row object is kept while
 * nothing it shows changes, so thousands of variables scroll and filter
 * without a pause. Each opening starts with nothing ticked and no search;
 * "Show categories as" stays as it was.
 */
const BuildFromDataDialog: React.FC<BuildFromDataDialogProps> = ({
  open,
  onClose,
  only = null,
  getContainer,
}) => {
  // Read only while open: a closed dialog does not render on every edit.
  const catalogue = useTabSpecStore((s): Catalogue | null => (open ? s.catalogue : null))
  const tables = useTabSpecStore((s) => (open ? s.document.tables : NO_TABLES))
  const defaults = useTabSpecStore((s): TabSpecDefaults | null =>
    open ? s.document.defaults : null
  )
  const labels = useTabSpecStore((s): ShownLabel[] | undefined =>
    open ? s.document.labels : undefined
  )
  const weight = useTabSpecStore((s) => (open ? s.document.data.weight : null))
  const storeDataOnly = useTabSpecStore((s) => s.dataOnly)
  const status = useTabSpecStore((s) => (open ? s.session.status : "idle"))
  const sessionError = useTabSpecStore((s) => (open ? s.session.error : undefined))
  const addTables = useTabSpecStore((s) => s.addTables)

  const [search, setSearch] = useState("")
  const [listing, setListing] = useState<Listing>("all")
  const [ticked, setTicked] = useState<React.Key[]>([])
  const [showAs, setShowAs] = useState<ShowAs>("multi")

  // Each opening starts afresh, from its first render: no search, nothing
  // ticked, the finding's variables without a table. "Show categories as"
  // stays.
  const [wasOpen, setWasOpen] = useState(false)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setSearch("")
      setTicked([])
      setListing(only ? "untabled" : "all")
    }
  }

  const dataOnly = storeDataOnly || catalogue?.dataOnly === true
  const notReady = open ? dataNotReady(status, sessionError, catalogue) : null
  const variables = useMemo(
    () => (catalogue?.dataVariables ?? []).filter((v) => v.label !== weight),
    [catalogue, weight]
  )
  const names = useMemo(() => shownNames(labels), [labels])
  /** How many tables of each variable the spec has. */
  const counts = useMemo(() => {
    const out = new Map<string, number>()
    tables.forEach((t) => {
      if (t.source === "data") out.set(t.question, (out.get(t.question) ?? 0) + 1)
    })
    return out
  }, [tables])

  // The rows as last built, by variable: a row keeps its object while
  // nothing it shows changes.
  const built = useRef(new Map<string, VariableRow>())
  const rows = useMemo((): VariableRow[] => {
    if (!defaults) return []
    const before = built.current
    const next = new Map<string, VariableRow>()
    const all = variables.map((q): VariableRow => {
      const shown = names.variable(q.label)
      const parts = dataVariableParts(q)
      const labelled = shown !== q.label
      const fresh: VariableRow = {
        key: q.label,
        question: q,
        name: labelled ? shown : parts.name,
        within: labelled ? null : parts.within,
        title: parts.path,
        phrase: describeDataVariable(q),
        tables: counts.get(q.label) ?? 0,
        // Only the number counts here: no id is made.
        adds: dataTablesFor(q, defaults, () => "").length,
        // The raw label, the shown one, and the words the row shows for a
        // nested variable or a grid across containers ("Spend grid: Price").
        search: [q.label, shown, parts.within ? `${parts.within}: ${parts.name}` : parts.name]
          .join("\n")
          .toLowerCase(),
      }
      const was = before.get(q.label)
      const row = was && sameRow(was, fresh) ? was : fresh
      next.set(q.label, row)
      return row
    })
    built.current = next
    return all
  }, [variables, names, counts, defaults])

  const onlySet = useMemo(() => (only ? new Set(only) : null), [only])
  const listed = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return rows.filter(
      (r) =>
        (!onlySet || onlySet.has(r.key)) &&
        (listing === "all" || r.tables === 0) &&
        (!needle || r.search.includes(needle))
    )
  }, [rows, onlySet, listing, search])

  const tickedSet = useMemo(() => new Set(ticked.map(String)), [ticked])
  // In the order of the data, whatever order they were ticked in.
  const chosen = useMemo(() => rows.filter((r) => tickedSet.has(r.key)), [rows, tickedSet])
  const tableCount = chosen.reduce((sum, r) => sum + r.adds, 0)
  const hasSingle = useMemo(() => listed.some((r) => r.question.answers === "single"), [listed])

  // "Select all listed" takes the variables listed that have no table yet,
  // and never a grid across containers: its rows are variables listed on
  // their own, so it is added deliberately, one tick at a time.
  const bulk = useMemo(() => listed.filter(inBulk), [listed])
  const tabled = useMemo(() => listed.filter((r) => r.tables > 0).length, [listed])
  const acrossContainers = useMemo(
    () => listed.filter((r) => r.tables === 0 && r.question.siblings).length,
    [listed]
  )
  const selectAllListed = (): void =>
    setTicked([...new Set([...ticked.map(String), ...bulk.map((r) => r.key)])])
  const leftOut = [
    ...(tabled > 0 ? [tabledText(tabled)] : []),
    ...(acrossContainers > 0 ? [acrossText(acrossContainers)] : []),
  ].join(" ")

  const add = (): void => {
    if (!defaults || chosen.length === 0) return
    addTables(chosen.flatMap((r) => dataTablesFor(r.question, defaults, undefined, showAs)))
    onClose()
  }

  const rowSelection = useMemo(
    () => ({
      columnWidth: TICK_WIDTH,
      hideSelectAll: true,
      // A tick stays while a search or "Without a table" hides its row.
      preserveSelectedRowKeys: true,
      selectedRowKeys: ticked,
      onChange: setTicked,
      // antd hands these to the input; its type does not list aria props.
      getCheckboxProps: (r: VariableRow) => ({ "aria-label": `Choose ${r.title}` }) as CheckboxProps,
    }),
    [ticked]
  )
  const onSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value), [])

  const hint = dataOnly ? HINT_DATA_ONLY : HINT_QUESTIONNAIRE
  let body: React.ReactNode
  if (notReady !== null) body = <p className="ts-hint ts-build__reason">{notReady}</p>
  else if (variables.length === 0)
    body = (
      <p className="ts-hint ts-build__reason">{dataOnly ? NONE_DATA_ONLY : NONE_QUESTIONNAIRE}</p>
    )
  else
    body = (
      <>
        <div className="ts-build__toolbar">
          <Input.Search
            allowClear
            size="small"
            className="ts-build__search"
            placeholder="Search variables"
            aria-label="Search variables"
            value={search}
            onChange={onSearch}
          />
          <Segmented<Listing>
            size="small"
            aria-label="Variables listed"
            value={listing}
            options={LISTINGS}
            onChange={setListing}
          />
        </div>
        <div className="ts-build__actions">
          <Button size="small" disabled={bulk.length === 0} onClick={selectAllListed}>
            Select all listed
          </Button>
          <Button size="small" disabled={ticked.length === 0} onClick={() => setTicked([])}>
            Clear
          </Button>
          <span className="ts-build__count" role="status">
            {chosenText(chosen.length, tableCount)}
          </span>
        </div>
        {leftOut && <p className="ts-hint ts-build__left-out">{leftOut}</p>}
        <div className="ts-build__list">
          <Table<VariableRow>
            size="small"
            virtual
            {...VIRTUAL_ROWS}
            pagination={false}
            rowKey="key"
            columns={columns}
            dataSource={listed}
            scroll={SCROLL}
            rowSelection={rowSelection}
            onRow={onRow}
            locale={{ emptyText: "No variable matches." }}
          />
        </div>
        <section className="ts-field" aria-label="Show categories as">
          <h3 className="ts-field__label">Show categories as</h3>
          <Segmented<ShowAs>
            size="small"
            aria-label="Show categories as"
            value={showAs}
            options={SHOW_AS}
            onChange={setShowAs}
          />
          {showAs === "categorical" && <p className="ts-hint">{SINGLE_HINT}</p>}
          {hasSingle && <p className="ts-hint">{SINGLE_IN_DATA}</p>}
        </section>
      </>
    )

  return (
    <Modal
      open={open}
      title="Build tables from the data"
      width={720}
      okText={tableCount === 0 ? "Add tables" : `Add ${plural(tableCount, "table", "tables")}`}
      okButtonProps={{ disabled: tableCount === 0 }}
      cancelText="Cancel"
      onOk={add}
      onCancel={onClose}
      getContainer={getContainer}
      zIndex={SPEC_DIALOG_Z_INDEX}
      destroyOnHidden
    >
      <div className="ts-build">
        <p className="ts-hint">{hint}</p>
        {body}
      </div>
    </Modal>
  )
}

export default BuildFromDataDialog
